import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Route, 
  Plus, 
  Upload, 
  FileText, 
  Loader2, 
  ChevronRight, 
  Target, 
  TrendingUp,
  Sparkles,
  Building2,
  MapPin,
  ArrowRight,
  Trash2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  X,
  Edit3,
  Package,
  ShieldAlert,
  Layers,
  ArrowUpRight
} from 'lucide-react';

export default function RouteIntelligence() {
  const navigate = useNavigate();
  const { get, post, put, del, loading } = useApi();
  
  // State
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBriefDialog, setShowBriefDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  
  // Create route form state
  const [routeName, setRouteName] = useState('');
  const [inputMethod, setInputMethod] = useState('paste');
  const [pasteContent, setPasteContent] = useState('');
  const [manualBusinesses, setManualBusinesses] = useState([{ 
    business_name: '', 
    location: '', 
    existing_services: '',
    industry: '',
    notes: '' 
  }]);
  const [csvFile, setCsvFile] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    business_name: '',
    location: '',
    existing_services: '',
    industry: '',
    notes: ''
  });
  const [editingOpportunityId, setEditingOpportunityId] = useState(null);
  
  // Add business form state
  const [showAddBusinessDialog, setShowAddBusinessDialog] = useState(false);
  const [addBusinessForm, setAddBusinessForm] = useState({
    business_name: '',
    location: '',
    existing_services: '',
    industry: '',
    notes: ''
  });

  // Fetch routes
  const fetchRoutes = useCallback(async () => {
    try {
      const data = await get('/route-intelligence/routes');
      setRoutes(data || []);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  }, [get]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Create route
  const handleCreateRoute = async () => {
    if (!routeName.trim()) {
      toast.error('Please enter a route name');
      return;
    }

    try {
      let result;
      
      if (inputMethod === 'paste') {
        if (!pasteContent.trim()) {
          toast.error('Please paste your business list');
          return;
        }
        result = await post('/route-intelligence/routes/paste', {
          route_name: routeName,
          paste_content: pasteContent
        });
      } else if (inputMethod === 'manual') {
        const validBusinesses = manualBusinesses.filter(b => b.business_name.trim());
        if (validBusinesses.length === 0) {
          toast.error('Please add at least one business');
          return;
        }
        result = await post('/route-intelligence/routes', {
          route_name: routeName,
          businesses: validBusinesses
        });
      } else if (inputMethod === 'csv') {
        if (!csvFile) {
          toast.error('Please upload a CSV file');
          return;
        }
        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('route_name', routeName);
        
        const token = localStorage.getItem('token');
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/route-intelligence/routes/csv?route_name=${encodeURIComponent(routeName)}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        result = await response.json();
        if (!response.ok) throw new Error(result.detail || 'Failed to upload CSV');
      }

      toast.success(result.message || 'Route created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchRoutes();
      
      // Auto-select and analyze the new route
      if (result.route_id) {
        handleSelectRoute(result.route_id);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create route');
    }
  };

  // Reset form
  const resetForm = () => {
    setRouteName('');
    setPasteContent('');
    setManualBusinesses([{ business_name: '', location: '', existing_services: '', industry: '', notes: '' }]);
    setCsvFile(null);
  };

  // Add manual business row
  const addManualBusiness = () => {
    setManualBusinesses([...manualBusinesses, { business_name: '', location: '', existing_services: '', industry: '', notes: '' }]);
  };

  // Update manual business
  const updateManualBusiness = (index, field, value) => {
    const updated = [...manualBusinesses];
    updated[index][field] = value;
    setManualBusinesses(updated);
  };

  // Remove manual business
  const removeManualBusiness = (index) => {
    if (manualBusinesses.length > 1) {
      setManualBusinesses(manualBusinesses.filter((_, i) => i !== index));
    }
  };

  // Select route and fetch details
  const handleSelectRoute = async (routeId) => {
    try {
      const data = await get(`/route-intelligence/routes/${routeId}`);
      setSelectedRoute(data);
    } catch (error) {
      toast.error('Failed to load route');
    }
  };

  // Analyze route
  const handleAnalyzeRoute = async () => {
    if (!selectedRoute) return;
    
    setAnalyzing(true);
    try {
      const result = await post(`/route-intelligence/routes/${selectedRoute.id}/analyze`);
      toast.success(result.message || 'Analysis complete');
      handleSelectRoute(selectedRoute.id);
    } catch (error) {
      toast.error(error.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  // Delete route
  const handleDeleteRoute = async (routeId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this route and all its analysis?')) return;
    
    try {
      await del(`/route-intelligence/routes/${routeId}`);
      toast.success('Route deleted');
      if (selectedRoute?.id === routeId) {
        setSelectedRoute(null);
      }
      fetchRoutes();
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  // View opportunity brief
  const handleViewBrief = async (opportunityId) => {
    try {
      const data = await get(`/route-intelligence/opportunities/${opportunityId}`);
      setSelectedOpportunity(data);
      setShowBriefDialog(true);
    } catch (error) {
      toast.error('Failed to load brief');
    }
  };

  // Edit business
  const handleEditBusiness = (opp) => {
    setEditingOpportunityId(opp.id);
    setEditForm({
      business_name: opp.business_name || '',
      location: opp.location || '',
      existing_services: opp.existing_services || '',
      industry: opp.industry || '',
      notes: opp.notes || ''
    });
    setShowEditDialog(true);
  };

  // Save edited business
  const handleSaveEdit = async () => {
    try {
      await put(`/route-intelligence/opportunities/${editingOpportunityId}`, editForm);
      toast.success('Business updated. Re-run analysis to generate new brief.');
      setShowEditDialog(false);
      handleSelectRoute(selectedRoute.id);
    } catch (error) {
      toast.error(error.message || 'Failed to update');
    }
  };

  // Re-analyze single business
  const handleReanalyzeSingle = async (opportunityId) => {
    setReanalyzing(true);
    try {
      const result = await post(`/route-intelligence/opportunities/${opportunityId}/reanalyze`);
      if (result.success) {
        toast.success('Analysis complete');
        handleSelectRoute(selectedRoute.id);
      } else {
        toast.error(result.message || 'Analysis failed');
      }
    } catch (error) {
      toast.error(error.message || 'Analysis failed');
    } finally {
      setReanalyzing(false);
    }
  };

  // Add business to route
  const handleAddBusiness = async () => {
    if (!addBusinessForm.business_name.trim()) {
      toast.error('Please enter a business name');
      return;
    }
    
    try {
      const result = await post(`/route-intelligence/routes/${selectedRoute.id}/businesses`, addBusinessForm);
      toast.success(result.message || 'Business added');
      setShowAddBusinessDialog(false);
      setAddBusinessForm({ business_name: '', location: '', existing_services: '', industry: '', notes: '' });
      handleSelectRoute(selectedRoute.id);
      fetchRoutes(); // Update route list with new count
    } catch (error) {
      toast.error(error.message || 'Failed to add business');
    }
  };

  // Delete business from route
  const handleDeleteBusiness = async (opportunityId, businessName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${businessName}" from this route?`)) return;
    
    try {
      await del(`/route-intelligence/opportunities/${opportunityId}`);
      toast.success(`Removed ${businessName}`);
      handleSelectRoute(selectedRoute.id);
      fetchRoutes(); // Update route list with new count
    } catch (error) {
      toast.error(error.message || 'Failed to remove business');
    }
  };

  // Convert to opportunity
  const handleConvertToOpportunity = async (opportunityId) => {
    try {
      const result = await post(`/route-intelligence/opportunities/${opportunityId}/convert`);
      toast.success(result.message);
      setShowBriefDialog(false);
      navigate(`/opportunity/${result.opportunity_id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to convert');
    }
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />Analyzed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-700"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Pending</Badge>;
    }
  };

  // Render secondary opportunities (handles both string array and object array)
  const renderSecondaryOpportunities = (opps) => {
    if (!opps || opps.length === 0) return null;
    
    return opps.map((opp, i) => {
      if (typeof opp === 'string') {
        return (
          <li key={i} className="flex items-start gap-2 text-stone-700">
            <TrendingUp className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            {opp}
          </li>
        );
      }
      // Object format: {service, type, reason}
      return (
        <li key={i} className="flex items-start gap-2 text-stone-700 p-2 bg-stone-50 rounded-lg">
          <TrendingUp className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">{opp.service}</span>
            {opp.type && (
              <Badge variant="outline" className={`ml-2 text-xs ${opp.type === 'GAP' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {opp.type}
              </Badge>
            )}
            {opp.reason && <p className="text-sm text-stone-500 mt-1">{opp.reason}</p>}
          </div>
        </li>
      );
    });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="route-intelligence-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Route className="h-6 w-6 text-amber-700" />
            </div>
            <h1 className="text-3xl font-light text-stone-800">Route Intelligence</h1>
          </div>
          <p className="text-stone-500 text-lg ml-14">Find revenue already sitting on your route.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Panel - Routes List */}
          <div className="lg:col-span-1">
            <Card className="border-stone-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-medium">Your Routes</CardTitle>
                  <Button 
                    onClick={() => setShowCreateDialog(true)}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="analyze-route-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Analyze My Route
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {routes.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <Route className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No routes yet</p>
                    <p className="text-xs mt-1">Create your first route to discover opportunities</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {routes.map(route => (
                      <div
                        key={route.id}
                        onClick={() => handleSelectRoute(route.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedRoute?.id === route.id 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-stone-200 hover:border-amber-300 hover:bg-stone-50'
                        }`}
                        data-testid={`route-item-${route.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-800 truncate">{route.route_name}</p>
                            <p className="text-xs text-stone-500 mt-0.5">
                              {route.business_count} businesses • {route.analyzed_count || 0} analyzed
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(route.status)}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteRoute(route.id, e)}
                              className="h-8 w-8 p-0 text-stone-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Route Details */}
          <div className="lg:col-span-2">
            {selectedRoute ? (
              <Card className="border-stone-200">
                <CardHeader className="border-b border-stone-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">{selectedRoute.route_name}</CardTitle>
                      <CardDescription>
                        {selectedRoute.business_count} businesses • {selectedRoute.analyzed_count || 0} analyzed
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowAddBusinessDialog(true)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="add-business-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Business
                      </Button>
                      <Button
                        onClick={handleAnalyzeRoute}
                        disabled={analyzing}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="run-analysis-btn"
                      >
                        {analyzing ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>
                        ) : (
                          <><Sparkles className="h-4 w-4 mr-2" />Run Analysis</>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[600px]">
                    <div className="p-4 space-y-3">
                      {selectedRoute.opportunities?.map(opp => (
                        <div
                          key={opp.id}
                          className="p-4 border border-stone-200 rounded-lg hover:border-amber-300 transition-colors"
                          data-testid={`opportunity-card-${opp.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">ACCOUNT</Badge>
                                {opp.status === 'completed' && opp.quick_win_score > 0 && (
                                  <span className={`text-sm font-semibold px-2 py-0.5 rounded ${getScoreColor(opp.quick_win_score)}`}>
                                    {opp.quick_win_score}
                                  </span>
                                )}
                                {opp.primary_opportunity_type && (
                                  <Badge variant="outline" className={`text-xs ${opp.primary_opportunity_type === 'GAP' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                    {opp.primary_opportunity_type}
                                  </Badge>
                                )}
                              </div>
                              <h3 className="font-medium text-stone-800">{opp.business_name}</h3>
                              <div className="flex items-center gap-1 text-sm text-stone-500 mt-1">
                                <MapPin className="h-3 w-3" />
                                {opp.location}
                              </div>
                              {opp.existing_services && (
                                <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                                  <Package className="h-3 w-3" />
                                  Current: {opp.existing_services}
                                </div>
                              )}
                              {opp.status === 'completed' && opp.primary_opportunity && (
                                <p className="text-sm text-amber-700 mt-2 line-clamp-2">
                                  <TrendingUp className="h-3 w-3 inline mr-1" />
                                  {opp.primary_opportunity}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {getStatusBadge(opp.status)}
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditBusiness(opp)}
                                  className="h-8 w-8 p-0 text-stone-400 hover:text-blue-600"
                                  title="Edit business details"
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleDeleteBusiness(opp.id, opp.business_name, e)}
                                  className="h-8 w-8 p-0 text-stone-400 hover:text-red-600"
                                  title="Remove from route"
                                  data-testid={`delete-business-btn-${opp.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                {opp.status === 'completed' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewBrief(opp.id)}
                                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                    data-testid={`view-brief-btn-${opp.id}`}
                                  >
                                    View Brief
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                  </Button>
                                )}
                                {opp.status === 'pending' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReanalyzeSingle(opp.id)}
                                    disabled={reanalyzing}
                                    className="text-stone-600"
                                  >
                                    {reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-stone-200 h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-medium text-stone-800 mb-2">Select a Route</h3>
                  <p className="text-stone-500 text-sm max-w-xs">
                    Choose an existing route or create a new one to discover revenue opportunities
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Create Route Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-route-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Route className="h-5 w-5 text-amber-600" />
                Analyze My Route
              </DialogTitle>
              <DialogDescription>
                Add businesses to your route. Include current services for better gap analysis.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Route Name</label>
                <Input
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="e.g., Monday Downtown Route"
                  className="mt-1"
                  data-testid="route-name-input"
                />
              </div>

              <Tabs value={inputMethod} onValueChange={setInputMethod}>
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="paste" className="text-sm">
                    <FileText className="h-4 w-4 mr-1" />
                    Paste List
                  </TabsTrigger>
                  <TabsTrigger value="manual" className="text-sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Manual Entry
                  </TabsTrigger>
                  <TabsTrigger value="csv" className="text-sm">
                    <Upload className="h-4 w-4 mr-1" />
                    Upload CSV
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="paste" className="mt-4">
                  <div>
                    <label className="text-sm font-medium text-stone-700">Paste your business list</label>
                    <p className="text-xs text-stone-500 mb-2">One business per line. Format: Business Name, Location, Current Services (optional)</p>
                    <Textarea
                      value={pasteContent}
                      onChange={(e) => setPasteContent(e.target.value)}
                      placeholder="Acme Corp, 123 Main St, Mats, Uniforms&#10;Best Restaurant, Downtown, Floor Mats&#10;City Gym, Oak Avenue"
                      rows={8}
                      className="font-mono text-sm"
                      data-testid="paste-content-input"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="manual" className="mt-4">
                  <div className="space-y-4">
                    {manualBusinesses.map((business, index) => (
                      <div key={index} className="p-3 border border-stone-200 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-stone-500">Business #{index + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeManualBusiness(index)}
                            disabled={manualBusinesses.length === 1}
                            className="h-6 w-6 p-0 text-stone-400 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={business.business_name}
                            onChange={(e) => updateManualBusiness(index, 'business_name', e.target.value)}
                            placeholder="Business Name *"
                            data-testid={`manual-business-name-${index}`}
                          />
                          <Input
                            value={business.location}
                            onChange={(e) => updateManualBusiness(index, 'location', e.target.value)}
                            placeholder="Location"
                            data-testid={`manual-business-location-${index}`}
                          />
                        </div>
                        <Input
                          value={business.existing_services}
                          onChange={(e) => updateManualBusiness(index, 'existing_services', e.target.value)}
                          placeholder="Current Services (e.g., Mats, Uniforms, Towels)"
                          data-testid={`manual-business-services-${index}`}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={business.industry}
                            onChange={(e) => updateManualBusiness(index, 'industry', e.target.value)}
                            placeholder="Industry (optional)"
                          />
                          <Input
                            value={business.notes}
                            onChange={(e) => updateManualBusiness(index, 'notes', e.target.value)}
                            placeholder="Notes (optional)"
                          />
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={addManualBusiness}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Business
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="csv" className="mt-4">
                  <div className="border-2 border-dashed border-stone-200 rounded-lg p-8 text-center">
                    <Upload className="h-10 w-10 text-stone-400 mx-auto mb-3" />
                    <p className="text-sm text-stone-600 mb-2">Upload a CSV file with your business list</p>
                    <p className="text-xs text-stone-500 mb-1">Required: business_name (or name, company)</p>
                    <p className="text-xs text-stone-500 mb-4">Optional: location, existing_services (or current_services, services, products), industry, notes</p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload">
                      <Button variant="outline" asChild>
                        <span>Select CSV File</span>
                      </Button>
                    </label>
                    {csvFile && (
                      <p className="text-sm text-green-600 mt-2">
                        <CheckCircle2 className="h-4 w-4 inline mr-1" />
                        {csvFile.name}
                      </p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateRoute}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="create-route-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Create Route</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Business Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md" data-testid="edit-business-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-blue-600" />
                Edit Business
              </DialogTitle>
              <DialogDescription>
                Update business details. Changes will require re-analysis.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Business Name</label>
                <Input
                  value={editForm.business_name}
                  onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Location</label>
                <Input
                  value={editForm.location}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Current Services</label>
                <p className="text-xs text-stone-500 mb-1">Services you currently provide to this account</p>
                <Textarea
                  value={editForm.existing_services}
                  onChange={(e) => setEditForm({...editForm, existing_services: e.target.value})}
                  placeholder="e.g., Mats, Uniforms, Shop Towels"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Industry</label>
                <Input
                  value={editForm.industry}
                  onChange={(e) => setEditForm({...editForm, industry: e.target.value})}
                  placeholder="e.g., Healthcare, Manufacturing, Restaurant"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Notes / Observations</label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="e.g., Dirty floors observed, wet entry area"
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Business Dialog */}
        <Dialog open={showAddBusinessDialog} onOpenChange={setShowAddBusinessDialog}>
          <DialogContent className="max-w-md" data-testid="add-business-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-amber-600" />
                Add Business to Route
              </DialogTitle>
              <DialogDescription>
                Add a new business to "{selectedRoute?.route_name}"
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Business Name *</label>
                <Input
                  value={addBusinessForm.business_name}
                  onChange={(e) => setAddBusinessForm({...addBusinessForm, business_name: e.target.value})}
                  placeholder="e.g., ABC Manufacturing"
                  className="mt-1"
                  data-testid="add-business-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Location</label>
                <Input
                  value={addBusinessForm.location}
                  onChange={(e) => setAddBusinessForm({...addBusinessForm, location: e.target.value})}
                  placeholder="e.g., 123 Industrial Way, Vancouver"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Current Services</label>
                <p className="text-xs text-stone-500 mb-1">Services you currently provide to this account</p>
                <Textarea
                  value={addBusinessForm.existing_services}
                  onChange={(e) => setAddBusinessForm({...addBusinessForm, existing_services: e.target.value})}
                  placeholder="e.g., Mats, Uniforms, Shop Towels"
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Industry</label>
                <Input
                  value={addBusinessForm.industry}
                  onChange={(e) => setAddBusinessForm({...addBusinessForm, industry: e.target.value})}
                  placeholder="e.g., Healthcare, Manufacturing, Restaurant"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Notes / Observations</label>
                <Textarea
                  value={addBusinessForm.notes}
                  onChange={(e) => setAddBusinessForm({...addBusinessForm, notes: e.target.value})}
                  placeholder="e.g., Dirty floors observed, wet entry area"
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowAddBusinessDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddBusiness}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="add-business-submit-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Business
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Opportunity Brief Dialog */}
        <Dialog open={showBriefDialog} onOpenChange={setShowBriefDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="brief-dialog">
            {selectedOpportunity && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">ACCOUNT OPPORTUNITY BRIEF</Badge>
                      <DialogTitle className="text-2xl">{selectedOpportunity.business_name}</DialogTitle>
                      <p className="text-stone-500 flex items-center gap-1 mt-1">
                        <MapPin className="h-4 w-4" />
                        {selectedOpportunity.location}
                      </p>
                      {selectedOpportunity.existing_services && (
                        <p className="text-blue-600 flex items-center gap-1 mt-1 text-sm">
                          <Package className="h-4 w-4" />
                          Current: {selectedOpportunity.existing_services}
                        </p>
                      )}
                    </div>
                    <div className={`text-3xl font-bold px-4 py-2 rounded-lg ${getScoreColor(selectedOpportunity.quick_win_score)}`}>
                      {selectedOpportunity.quick_win_score}
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-6 mt-6">
                  {/* Business Summary */}
                  {selectedOpportunity.business_summary && (
                    <section>
                      <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">Business Summary</h3>
                      <p className="text-stone-700">{selectedOpportunity.business_summary}</p>
                    </section>
                  )}

                  {/* GAP BRIEFING - Cintas-specific section */}
                  {selectedOpportunity.gap_briefing && Object.keys(selectedOpportunity.gap_briefing).length > 0 && (
                    <section className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-red-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4" />
                        GAP BRIEFING
                      </h3>
                      
                      {selectedOpportunity.gap_briefing.current_services_identified?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-stone-600 mb-1">Current Services Identified:</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedOpportunity.gap_briefing.current_services_identified.map((s, i) => (
                              <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200">{s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedOpportunity.gap_briefing.missing_categories?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-red-700 mb-1">Missing Categories (GAPS):</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedOpportunity.gap_briefing.missing_categories.map((s, i) => (
                              <Badge key={i} className="bg-red-100 text-red-800">{s}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedOpportunity.gap_briefing.expansion_opportunities?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-blue-700 mb-1">Expansion Opportunities:</p>
                          <ul className="text-sm text-stone-700 space-y-1">
                            {selectedOpportunity.gap_briefing.expansion_opportunities.map((s, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <ArrowUpRight className="h-3 w-3 text-blue-600 mt-1 flex-shrink-0" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {selectedOpportunity.gap_briefing.cross_sell_opportunities?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-amber-700 mb-1">Cross-Sell Opportunities:</p>
                          <ul className="text-sm text-stone-700 space-y-1">
                            {selectedOpportunity.gap_briefing.cross_sell_opportunities.map((s, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <Layers className="h-3 w-3 text-amber-600 mt-1 flex-shrink-0" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {selectedOpportunity.gap_briefing.risk_areas?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-red-700 mb-1">Risk Areas:</p>
                          <ul className="text-sm text-red-900 space-y-1">
                            {selectedOpportunity.gap_briefing.risk_areas.map((s, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <AlertCircle className="h-3 w-3 text-red-600 mt-1 flex-shrink-0" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Core Need */}
                  {selectedOpportunity.core_need && (
                    <section className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide mb-2">Core Need</h3>
                      <p className="text-amber-900 font-medium">{selectedOpportunity.core_need}</p>
                    </section>
                  )}

                  {/* Primary Opportunity */}
                  {selectedOpportunity.primary_opportunity && (
                    <section className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wide mb-2 flex items-center gap-2">
                        Primary Opportunity
                        {selectedOpportunity.primary_opportunity_type && (
                          <Badge className={`${selectedOpportunity.primary_opportunity_type === 'GAP' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                            {selectedOpportunity.primary_opportunity_type}
                          </Badge>
                        )}
                      </h3>
                      <p className="text-green-900 font-medium">{selectedOpportunity.primary_opportunity}</p>
                    </section>
                  )}

                  {/* Secondary Opportunities */}
                  {selectedOpportunity.secondary_opportunities?.length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">Secondary Opportunities</h3>
                      <ul className="space-y-2">
                        {renderSecondaryOpportunities(selectedOpportunity.secondary_opportunities)}
                      </ul>
                    </section>
                  )}

                  {/* Missing Services (fallback if no gap_briefing) */}
                  {!selectedOpportunity.gap_briefing && selectedOpportunity.missing_services?.length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">Missing Services / Gaps</h3>
                      <ul className="space-y-2">
                        {selectedOpportunity.missing_services.map((service, i) => (
                          <li key={i} className="flex items-start gap-2 text-stone-700">
                            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            {service}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {/* Talking Points */}
                  {selectedOpportunity.talking_points && (
                    <section className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wide mb-2">Sales Talking Points</h3>
                      <p className="text-stone-700 whitespace-pre-wrap">{selectedOpportunity.talking_points}</p>
                    </section>
                  )}

                  {/* Objection Handling */}
                  {selectedOpportunity.objection_handling?.length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">Objection Handling</h3>
                      <div className="space-y-3">
                        {selectedOpportunity.objection_handling.map((obj, i) => (
                          <div key={i} className="border border-stone-200 rounded-lg p-3">
                            <p className="text-sm font-medium text-red-700 mb-1">"{obj.objection}"</p>
                            <p className="text-sm text-stone-700">{obj.response}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Elite Insight */}
                  {selectedOpportunity.elite_insight && (
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide mb-2">
                        <Sparkles className="h-4 w-4 inline mr-1" />
                        Elite Insight
                      </h3>
                      <p className="text-amber-900">{selectedOpportunity.elite_insight}</p>
                    </section>
                  )}

                  {/* Score Reason */}
                  {selectedOpportunity.score_reason && (
                    <section className="text-sm text-stone-500 border-t border-stone-200 pt-4">
                      <span className="font-medium">Score Rationale:</span> {selectedOpportunity.score_reason}
                    </section>
                  )}
                </div>

                <DialogFooter className="mt-6 pt-4 border-t border-stone-200">
                  <Button variant="outline" onClick={() => setShowBriefDialog(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => handleConvertToOpportunity(selectedOpportunity.id)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="convert-to-opportunity-btn"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Add to Pipeline
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </div>
    </Layout>
  );
}
